import React, { useMemo, useCallback, useState } from 'react';
import { usePrintSettings } from '../../context/PrintSettingsContext';
import { challanPrintOverrideCss } from '../../utils/challanPrintLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useRole } from '../../context/roleContext';
import {
  faEdit,
  faTrash,
  faEye,
  faCheck,
  faTimes,
  faShoppingCart,
  faSpinner,
  faTruck,
  faPrint,
  faArrowRight,
  faHistory,
} from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { escapeHtml } from '../../utils/escapeHtml';
import { companyHeader } from '../../utils/company';
import { AUDIT_LOG_VIEWER_ROLES } from '../../utils/constants';
import DataTable from '../common/DataTable';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from '../common/ConfirmDialog';
import { useMessageAlert } from '../common/MessageAlert';
import ordersService from '../../services/ordersService';
import { formatCurrency } from '../../utils/format';
import AuditLogsModal from '../common/AuditLogsModal';
import {
  canEditChallanRecord,
  canDeleteChallanRecord,
  editDeniedReason,
  deleteDeniedReason,
} from '../../utils/challanRbac';


const formatDate = (dateString, emDash) => {
  if (!dateString) return emDash;
  return new Date(dateString).toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const getStatusBadge = (status, t) => {
  const s = (status || '').toString().toLowerCase();
  const statuses = t('ordersModule.orders.statuses', { returnObjects: true });
  const getLabel = (key, fallback) => {
    if (!key) return fallback || String(status);
    return (statuses && statuses[key]) || fallback || String(status);
  };
  if (s === 'approved' || s === 'completed' || s === 'delivered' || s === 'invoiced' || s === 'confirmed') {
    return (
      <div className="flex items-center space-x-2">
        <FontAwesomeIcon icon={faCheck} className="text-sm" style={{ color: UI_COLORS.STATUS.SUCCESS }} />
        <span className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.SUCCESS }}>{getLabel(s, String(status))}</span>
      </div>
    );
  }
  if (s === 'rejected' || s === 'cancelled' || s === 'failed') {
    return (
      <div className="flex items-center space-x-2">
        <FontAwesomeIcon icon={faTimes} className="text-sm" style={{ color: UI_COLORS.STATUS.ERROR }} />
        <span className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.ERROR }}>{getLabel(s, String(status))}</span>
      </div>
    );
  }
  if (s === 'delivery_assigned') {
    return (
      <div className="flex items-center space-x-2">
        <FontAwesomeIcon icon={faTruck} className="text-sm" style={{ color: UI_COLORS.STATUS.INFO }} />
        <span className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.INFO }}>{getLabel(s, 'Delivery Assigned')}</span>
      </div>
    );
  }
  // in_transit: show as truck/in-progress
  if (s === 'in_transit') {
    return (
      <div className="flex items-center space-x-2">
        <FontAwesomeIcon icon={faTruck} className="text-sm" style={{ color: UI_COLORS.STATUS.INFO }} />
        <span className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.INFO }}>{getLabel(s, 'In Transit')}</span>
      </div>
    );
  }
  // loading / in-progress statuses
  if (['loading', 'loading_started', 'truck_loading', 'truck_load_started', 'truck_loading_started', 'loading_in_progress', 'truck_load'].includes(s)) {
    return (
      <div className="flex items-center space-x-2">
        <FontAwesomeIcon icon={faSpinner} spin className="text-sm" style={{ color: UI_COLORS.STATUS.INFO }} />
        <span className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.INFO }}>{getLabel(s, statuses.loading || String(status))}</span>
      </div>
    );
  }
  // truck loaded / finished
  if (s === 'truck_loaded' || s === 'loaded' || s === 'truck_load_completed') {
    return (
      <div className="flex items-center space-x-2">
        <FontAwesomeIcon icon={faCheck} className="text-sm" style={{ color: UI_COLORS.STATUS.SUCCESS }} />
        <span className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.SUCCESS }}>{getLabel(s, 'Truck Loaded')}</span>
      </div>
    );
  }
  // pending / default
  return (
    <div className="flex items-center space-x-2">
      <FontAwesomeIcon icon={faShoppingCart} className="text-sm" style={{ color: UI_COLORS.STATUS.WARNING }} />
      <span className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.WARNING }}>{getLabel(s, String(status))}</span>
    </div>
  );
};

const Orderstable = ({
  orders = [],
  loading = false,
  selectedOrders = [],
  onSelectOrder,
  onSelectAll,
  onEditOrder,
  onDeleteOrder,
  onViewOrder,
  onAssignPersonnel,
  onStartTruckLoading,
  onRefresh,
  viewLoading = false,
}) => {
  const { t } = useTranslation();

  const [printingOrderId, setPrintingOrderId] = useState(null);
  const [printingInvoiceId, setPrintingInvoiceId] = useState(null);
  const [showConfirmOrder, setShowConfirmOrder] = useState(false);
  const [confirmOrderId, setConfirmOrderId] = useState(null);
  const [globalLoading, setGlobalLoading] = useState(false);
  const [auditLogTarget, setAuditLogTarget] = useState(null);
  const { showSuccess, showError } = useMessageAlert();
  const hoverColor = useMemo(() => colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.SECTION, 0.5), []);
  // Generate-challan / mark-delivered / cancel moved to the Delivery Orders page
  // (sub-order flow); their order-level routes no longer exist.
  const { checkDeleteAccess, checkEditAccess, userRole } = useRole();
  const { deliveryChallanPaperSize: challanPaperSize } = usePrintSettings();

  // Challan state for a sales order. `first_challan_date` is the EARLIEST challan
  // across the order's sub-orders, so "it is today" means every challan on the order
  // is — see Order.first_challan_date on the API. Both fields come from
  // OrderListResponse; if an older API build omits them the record reads as
  // challan-generated with an unknown date, which locks it rather than opening it up.
  const challanStateOf = useCallback((order) => ({
    userRole,
    // Fail CLOSED when the field is absent. `challan_generated` is derived server-side
    // from Order.has_challan; an API build that predates it would otherwise read as
    // "no challan" and offer Edit/Delete on challaned orders that the server refuses.
    challanGenerated: order?.challan_generated ?? true,
    challanDate: order?.first_challan_date,
  }), [userRole]);

  // Shared rule (src/utils/challanRbac.js), mirroring app/utils/challan_rbac.py:
  // pre-challan this is ordinary write access; post-challan it is Super Admin only,
  // and for edits only on the challan's own IST day.
  const canEditOrderRow = useCallback((order) => {
    if (!canEditChallanRecord(challanStateOf(order))) return false;
    return checkEditAccess(order?.date, { isOrder: true });
  }, [challanStateOf, checkEditAccess]);

  const canDeleteOrderRow = useCallback((order) => {
    if (!checkDeleteAccess()) return false;
    return canDeleteChallanRecord(challanStateOf(order));
  }, [challanStateOf, checkDeleteAccess]);

  // When the challan rule is what blocks the action, keep the button visible but
  // disabled with the reason — a silently missing button reads as a broken screen.
  const editBlockedReasonRow = useCallback(
    (order) => editDeniedReason(challanStateOf(order)) || null, [challanStateOf]);
  const deleteBlockedReasonRow = useCallback((order) => {
    if (!checkDeleteAccess()) return null;
    return deleteDeniedReason(challanStateOf(order)) || null;
  }, [challanStateOf, checkDeleteAccess]);

  const handlePrintChallan = useCallback(async (orderOrId) => {
    // Always fetch fresh order details by id before printing
    const id = typeof orderOrId === 'object' ? orderOrId.id : orderOrId;
    if (!id) return;
    setPrintingOrderId(id);
    let order = null;
    try {
      const res = await ordersService.getOrderById(id, { force: true });
      if (res && res.success) order = res.data;
    } catch (e) { }

    if (!order) {
      if (typeof orderOrId === 'object') order = orderOrId;
    }

    if (!order) {
      setPrintingOrderId(null);
      alert(t('common.messages.unableToLoadOrderForPrinting'));
      return;
    }

    const safe = (v) => escapeHtml(v); // written into a print window (P1-2)
    const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '');
    const fmtTime = (d) => (d ? new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }) : '');
    const nowTime = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });

    const items = Array.isArray(order.items) ? order.items.filter(it => it && Object.keys(it).length > 0) : [];

    // HSN Summary Calculation
    const hsnSummaryMap = items.reduce((acc, it) => {
      const hsn = safe(it.hsn_code) || 'N/A';
      if (!acc[hsn]) {
        acc[hsn] = { qty: 0, amount: 0, gst: Number(it.gst_percentage) || 0, unit: safe(it.unit) };
      }
      acc[hsn].qty += Number(it.quantity) || 0;
      acc[hsn].amount += (Number(it.price) || 0) * (Number(it.quantity) || 0);
      return acc;
    }, {});

    const itemsRows = items.map((it, idx) => {
      // Use actual values from API instead of calculating
      const itemAmount = Number(it.amount) || 0;
      const gstAmount = Number(it.gst_amount) || 0;
      const actualPieces = it.actual_pieces || '';
      return `
      <tr>
        <td>${idx + 1}</td>
        <td>
          <div>${safe(it.size_name || it.product_name)}</div>
          ${it.coil_tracked && safe(it.length_note).trim()
            ? `<div style="font-size: 6px; color: #444; margin-top: 1px;"><b>Length:</b> ${safe(it.length_note).trim()}</div>`
            : ''}
        </td>
        <td>${safe(it.hsn_code)}</td>
        <td>${actualPieces}</td>
        <td>${safe(it.pending_order)} ${safe(it.unit)}</td>
        <td>${safe(it.actual_weight)} ${safe(it.unit)}</td>
        <td>${safe(it.gst_percentage)}</td>
        <td class="text-right">${formatCurrency(it.price || 0)}</td>
        <td class="text-right">${formatCurrency(gstAmount)}</td>
        <td class="text-right">${formatCurrency(itemAmount)}</td>
      </tr>`;
    }).join('\n');

    const hsnRows = Object.entries(hsnSummaryMap).map(([hsn, data]) => {
      const gstAmt = (data.amount * data.gst) / 100;
      return `
      <tr>
        <td>${hsn}</td>
        <td>${data.qty} ${data.unit}</td>
        <td>${formatCurrency(Math.round(data.amount))}</td>
        <td>${data.gst}</td>
        <td>${formatCurrency(gstAmt)}</td>
        <td>${formatCurrency(data.amount + gstAmt)}</td>
      </tr>`;
    }).join('\n');

    // Use actual values from API instead of calculating
    const totalItemAmount = Number(order.subtotal) || 0;
    const loading = Number(order.total_loading_cost) || 0;
    const loadingGst = Number(order.loading_cost_gst_amount) || 0;
    const freight = Number(order.freight) || 0;
    const freightGst = Number(order.freight_gst_amount) || 0;
    const totalGst = Number(order.total_gst_amount) || 0;
    const netTotal = Number(order.total_amount) || 0;

    // Freight type billing logic
    const freightTypePrint = order.freight_type || '';
    const isFreightChargeablePrint = freightTypePrint === 'freight_chargeable';
    const isFreightToPayPrint = freightTypePrint === 'free_delivery_freight_to_pay';
    const showFreightRowPrint = !!freightTypePrint;
    const freightPricingTypePrint = order.freight_pricing_type || '';
    const freightLabelPrint = isFreightChargeablePrint
      ? `Freight Chargeable${freightPricingTypePrint === 'per_kg' ? ' (Per KG)' : freightPricingTypePrint === 'fixed' ? ' (Fixed)' : ''}`
      : isFreightToPayPrint ? 'Freight To Pay'
      : freightTypePrint === 'ex_warehouse' ? 'Ex Warehouse'
      : freightTypePrint === 'free_delivery_by_supplier' ? 'Free by Supplier'
      : freightTypePrint === 'free_delivery_paid_by_company' ? 'Freight Paid by Company'
      : 'Freight';
    const freightValuePrint = isFreightToPayPrint
      ? `- ${formatCurrency(freight)}`
      : `${formatCurrency(freight)}`;
    const freightColorPrint = isFreightToPayPrint ? 'color: #059669;' : (!isFreightChargeablePrint ? 'color: #9ca3af;' : '');

    // Format final truck weight - don't show "KG" if empty
    const finalTruckWeightDisplay = order.final_truck_weight ? `${safe(order.final_truck_weight)} KG` : '';

    const companyDetailsHtml = [
      process.env.REACT_APP_COMPANY_ADDRESS ? `<div style="text-align:center;font-size:8px;color:#555;margin-bottom:1px;">${process.env.REACT_APP_COMPANY_ADDRESS}</div>` : '',
      (process.env.REACT_APP_COMPANY_GST || process.env.REACT_APP_COMPANY_PAN) ? `<div style="text-align:center;font-size:8px;color:#555;margin-bottom:1px;">${[process.env.REACT_APP_COMPANY_GST ? 'GST: ' + process.env.REACT_APP_COMPANY_GST : '', process.env.REACT_APP_COMPANY_PAN ? 'PAN: ' + process.env.REACT_APP_COMPANY_PAN : ''].filter(Boolean).join(' | ')}</div>` : '',
      process.env.REACT_APP_COMPANY_BANK_DETAILS ? `<div style="text-align:center;font-size:8px;color:#555;margin-bottom:1px;">${process.env.REACT_APP_COMPANY_BANK_DETAILS}</div>` : '',
    ].filter(Boolean).join('');

    const html = `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Delivery Challan - ${safe(order.order_number)}</title>
  <style>
    @page { 
      size: A5;
      margin: 8mm;
    }
    * { box-sizing: border-box; font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif; color: #333; }
    body { margin: 0; padding: 0; background: #fff; line-height: 1.2; font-size: 9px; }
    .no-print { padding: 10px; background: #f0f0f0; border-bottom: 1px solid #ddd; }
    .print-container { max-width: 148mm; margin: 0 auto; padding: 8mm; }
    .title { text-align: center; font-size: 12px; font-weight: 700; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px; }
    
    .header-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 2px 8px; margin-bottom: 8px; font-size: 8px; }
    .header-field { display: flex; justify-content: flex-start; gap: 4px; margin-bottom: 1px; }
    .header-label { font-weight: 600; min-width: 50px; }
    
    table { width: 100%; border-collapse: collapse; margin-bottom: 6px; font-size: 7px; border: 1px solid #ddd; }
    th, td { padding: 2px 3px; text-align: left; border: 1px solid #ddd; }
    th { background: #f5f5f5; font-weight: 700; color: #555; text-transform: uppercase; font-size: 6px; letter-spacing: 0.3px; }
    .text-right { text-align: right; }
    
    .footer-section { display: grid; grid-template-columns: 1.5fr 1fr; gap: 8px; margin-top: 6px; align-items: flex-start; font-size: 7px; }
    .total-qty-row { font-size: 8px; font-weight: 700; margin-bottom: 4px; }
    .remarks-box { font-size: 7px; margin-top: 4px; color: #666; }
    .remarks-label { font-weight: 600; color: #444; margin-bottom: 2px; display: block; }
    
    .hsn-summary-title { font-size: 8px; font-weight: 700; margin-bottom: 4px; text-transform: uppercase; color: #444; }
    .hsn-table td, .hsn-table th { padding: 2px 3px; font-size: 7px; }
    
    .summary-table { width: 100%; border: none; }
    .summary-table td { padding: 2px 0; border: none; font-size: 8px; }
    .summary-table .label { color: #555; }
    .summary-table .value { text-align: right; font-weight: 600; }
    .summary-table .gst-row td { color: #444; font-size: 7px; padding: 2px 0; }
    .summary-table .net-total-row td { border-top: 2px solid #333; font-weight: 800; font-size: 9px; padding-top: 4px; }
    
    @media print { 
      .no-print { display: none !important; } 
      .print-container { padding: 0; }
      body { font-size: 9px; }
    }
  ${challanPrintOverrideCss(challanPaperSize, 'portrait')}
  </style>
</head>
<body>
  <div class="no-print"><button onclick="window.print()" style="padding: 10px 20px; cursor:pointer;">Print Challan</button></div>
  <div class="print-container">
    ${!order.gst_included ? `
      <div style="text-align: center; font-weight: bold; font-size: 14px; margin-bottom: 2px;">${safe(companyHeader(order.warehouse_name))}</div>
      ${companyDetailsHtml}
      <div class="title" style="margin-bottom: 12px;">Delivery Challan</div>
    ` : `
      <div class="title">Delivery Challan</div>
    `}
    
    <div class="header-grid">
      ${order.customer_name ? `<div class="header-field"><span class="header-label">Name:</span> <span>${safe(order.customer_name)}</span></div>` : ''}
      ${order.date ? `<div class="header-field"><span class="header-label">Date:</span> <span>${fmt(order.date)}</span></div>` : ''}
      ${order.price_list_date ? `<div class="header-field"><span class="header-label">Price List Date:</span> <span>${fmt(order.price_list_date)}</span></div>` : ''}
      ${(order.created_at || order.date) ? `<div class="header-field"><span class="header-label">Booking Date:</span> <span>${fmt(order.created_at || order.date)}</span></div>` : ''}
      ${order.delivery_location_name ? `<div class="header-field"><span class="header-label">Delivery @:</span> <span>${safe(order.delivery_location_name)}</span></div>` : ''}
      ${order.challan_number ? `<div class="header-field"><span class="header-label">Challan Serial:</span> <span>${safe(order.challan_number)}</span></div>` : ''}
      
      ${(order.created_at || order.date) ? `<div class="header-field"><span class="header-label">Time Created:</span> <span>${fmtTime(order.created_at || order.date)}</span></div>` : ''}
      <div class="header-field"><span class="header-label">Time Print:</span> <span>${nowTime}</span></div>
      
      ${order.vehicle_number ? `<div class="header-field"><span class="header-label">Truck Number:</span> <span>${safe(order.vehicle_number)}</span></div>` : ''}
      ${order.driver_contact ? `<div class="header-field"><span class="header-label">Driver Contact:</span> <span>${safe(order.driver_contact)}</span></div>` : ''}
      
      ${order.empty_truck_weight ? `<div class="header-field"><span class="header-label">Empty Truck Weight:</span> <span>${safe(order.empty_truck_weight)} KG</span></div>` : ''}
      ${finalTruckWeightDisplay ? `<div class="header-field"><span class="header-label">Final Truck Weight:</span> <span>${finalTruckWeightDisplay}</span></div>` : ''}
    </div>

    <table>
      <thead>
        <tr>
          <th style="width: 40px;">Sr.</th>
          <th>Size</th>
          <th style="width: 80px;">HSN</th>
          <th style="width: 60px;">Pcs</th>
          <th style="width: 100px;">Qty</th>
          <th style="width: 100px;">Actual Qty</th>
          <th style="width: 60px;">IGST</th>
          <th class="text-right" style="width: 100px;">Rate</th>
          <th class="text-right" style="width: 120px;">GST Amount</th>
          <th class="text-right" style="width: 120px;">Amount</th>
        </tr>
      </thead>
      <tbody>
        ${itemsRows}
      </tbody>
    </table>

    <div class="footer-section">
      <div class="left-col">
        <div class="total-qty-row">Total Quantity: ${safe(order.total_weight)} KG</div>

        ${order.remark ? `<div class="remarks-box"><span class="remarks-label">Remarks:</span> ${safe(order.remark)}</div>` : ''}

        <div class="hsn-summary">
          <div class="hsn-summary-title">HSN Summary</div>
          <table class="hsn-table">
            <thead>
              <tr>
                <th>HSN Code</th>
                <th>Qty</th>
                <th>Amount</th>
                <th>GST %</th>
                <th>GST Amount</th>
                <th>Total Inc GST</th>
              </tr>
            </thead>
            <tbody>
              ${hsnRows}
            </tbody>
          </table>
        </div>
      </div>

      <div class="right-col">
        <table class="summary-table">
          <tr>
            <td class="label">Subtotal</td>
            <td class="value">${formatCurrency(totalItemAmount)}</td>
          </tr>
          ${loading && parseFloat(loading) > 0 ? `
          <tr>
            <td class="label">Loading</td>
            <td class="value">${formatCurrency(loading)}</td>
          </tr>` : ''}
          ${loadingGst && parseFloat(loadingGst) > 0 ? `
          <tr>
            <td class="label" style="padding-left: 10px; font-size: 7px;">Loading GST</td>
            <td class="value" style="font-size: 7px;">${formatCurrency(loadingGst)}</td>
          </tr>` : ''}
          ${showFreightRowPrint ? `
          <tr>
            <td class="label" style="${freightColorPrint}">${freightLabelPrint}</td>
            <td class="value" style="${freightColorPrint}">${freightValuePrint}</td>
          </tr>` : ''}
          ${isFreightChargeablePrint && freightGst > 0 ? `
          <tr>
            <td class="label" style="padding-left: 10px; font-size: 7px; color: #6b7280;">Freight GST</td>
            <td class="value" style="font-size: 7px; color: #6b7280;">${formatCurrency(freightGst)}</td>
          </tr>` : ''}
          ${order.total_sub_order_expenses && parseFloat(order.total_sub_order_expenses) > 0 ? `
          <tr>
            <td class="label">Expenses</td>
            <td class="value">${formatCurrency(order.total_sub_order_expenses)}</td>
          </tr>` : ''}
          ${order.total_sub_order_discount && parseFloat(order.total_sub_order_discount) > 0 ? `
          <tr>
            <td class="label">Discount</td>
            <td class="value">- ${formatCurrency(order.total_sub_order_discount)}</td>
          </tr>` : ''}
          <tr class="gst-row">
            <td class="label">Total GST = IGST</td>
            <td class="value">${formatCurrency(totalGst)}</td>
          </tr>
          <tr class="net-total-row">
            <td class="label">Total (with GST)</td>
            <td class="value">${formatCurrency(netTotal)}</td>
          </tr>
        </table>
      </div>
    </div>
  </div>
</body>
</html>`;

    const w = window.open('', '_blank');
    if (!w) {
      setPrintingOrderId(null);
      alert(t('common.messages.unableToOpenPrintWindow'));
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
    try { w.document.title = ''; } catch (e) { }
    setTimeout(() => { try { w.focus(); w.print(); } catch (e) { } finally { setPrintingOrderId(null); } }, 300);
  }, [t, challanPaperSize]);

  const handlePrintDeliveryOrder = useCallback(async (orderOrId) => {
    // Always fetch fresh order details by id before printing
    const id = typeof orderOrId === 'object' ? orderOrId.id : orderOrId;
    if (!id) return;
    setPrintingOrderId(id);
    let order = null;
    try {
      const res = await ordersService.getOrderById(id, { force: true });
      if (res && res.success) order = res.data;
    } catch (e) { }

    if (!order) {
      if (typeof orderOrId === 'object') order = orderOrId;
    }

    if (!order) {
      setPrintingOrderId(null);
      alert(t('common.messages.unableToLoadOrderForPrinting'));
      return;
    }

    const safe = (v) => escapeHtml(v); // written into a print window (P1-2)
    const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '');
    const fmtTime = (d) => (d ? new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }) : '');
    const nowTime = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });

    const items = Array.isArray(order.items) ? order.items.filter(it => it && Object.keys(it).length > 0) : [];

    const itemsRows = items.map((it, idx) => {
      // Show pieces only if quantity_in_pieces is not null
      const pcs = (it.quantity_in_pieces !== null && it.quantity_in_pieces !== undefined) ? safe(it.quantity_in_pieces) : '';
      return `
      <tr>
        <td>${idx + 1}</td>
        <td>
          ${safe(it.size_name || it.product_name)}
          ${it.coil_tracked && safe(it.length_note).trim()
            ? `<div style="font-size: 6px; color: #444; margin-top: 1px;"><b>Length:</b> ${safe(it.length_note).trim()}</div>`
            : ''}
        </td>
        <td>${pcs}</td>
        <td>${safe(it.quantity)} ${safe(it.unit)}</td>
        <td></td>
        <td></td>
      </tr>`;
    }).join('\n');

    const totalQtyUnit = items[0]?.unit || 'KG';
    const totalQty = items.reduce((sum, it) => sum + (Number(it.quantity) || 0), 0);

    // Format final truck weight - don't show "KG" if empty
    const finalTruckWeightDisplay = order.final_truck_weight ? `${safe(order.final_truck_weight)} KG` : '';

    const html = `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Delivery Order - ${safe(order.order_number)}</title>
  <style>
    @page { 
      size: A5;
      margin: 8mm;
    }
    * { box-sizing: border-box; font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif; color: #333; }
    body { margin: 0; padding: 0; background: #fff; line-height: 1.2; font-size: 9px; }
    .no-print { padding: 10px; background: #f0f0f0; border-bottom: 1px solid #ddd; }
    .print-container { max-width: 148mm; margin: 0 auto; padding: 8mm; }
    .title { text-align: center; font-size: 12px; font-weight: 700; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px; }
    
    .header-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 2px 8px; margin-bottom: 8px; font-size: 8px; }
    .header-field { display: flex; justify-content: flex-start; gap: 4px; margin-bottom: 1px; }
    .header-label { font-weight: 600; min-width: 50px; }
    
    table { width: 100%; border-collapse: collapse; margin-bottom: 6px; font-size: 8px; border: 1px solid #ddd; }
    th, td { padding: 3px 4px; text-align: left; border: 1px solid #ddd; }
    th { background: #f5f5f5; font-weight: 700; color: #555; text-transform: uppercase; font-size: 7px; letter-spacing: 0.3px; }
    .text-right { text-align: right; }
    
    .total-qty-row { font-size: 9px; font-weight: 700; margin-top: 6px; margin-bottom: 4px; }
    
    @media print { 
      .no-print { display: none !important; } 
      .print-container { padding: 0; }
      body { font-size: 9px; }
    }
  </style>
</head>
<body>
  <div class="no-print"><button onclick="window.print()" style="padding: 10px 20px; cursor:pointer;">Print Delivery Order</button></div>
  <div class="print-container">
    <div class="title">Delivery Order</div>
    
    <div class="header-grid">
      ${order.customer_name ? `<div class="header-field"><span class="header-label">Name:</span> <span>${safe(order.customer_name)}</span></div>` : ''}
      ${order.date ? `<div class="header-field"><span class="header-label">Date:</span> <span>${fmt(order.date)}</span></div>` : ''}
      ${order.price_list_date ? `<div class="header-field"><span class="header-label">Price List Date:</span> <span>${fmt(order.price_list_date)}</span></div>` : ''}
      ${(order.created_at || order.date) ? `<div class="header-field"><span class="header-label">Booking Date:</span> <span>${fmt(order.created_at || order.date)}</span></div>` : ''}
      ${order.delivery_location_name ? `<div class="header-field"><span class="header-label">Delivery @:</span> <span>${safe(order.delivery_location_name)}</span></div>` : ''}
      ${order.order_number ? `<div class="header-field"><span class="header-label">Order #:</span> <span>${safe(order.order_number)}</span></div>` : ''}
      
      ${order.vehicle_number ? `<div class="header-field"><span class="header-label">Truck #:</span> <span>${safe(order.vehicle_number)}</span></div>` : ''}
      ${order.driver_contact ? `<div class="header-field"><span class="header-label">Driver:</span> <span>${safe(order.driver_contact)}</span></div>` : ''}
      
      ${(order.created_at || order.date) ? `<div class="header-field"><span class="header-label">Created:</span> <span>${fmtTime(order.created_at || order.date)}</span></div>` : ''}
      <div class="header-field"><span class="header-label">Printed:</span> <span>${nowTime}</span></div>
      
      ${order.empty_truck_weight ? `<div class="header-field"><span class="header-label">Empty Wt:</span> <span>${safe(order.empty_truck_weight)} KG</span></div>` : ''}
      ${finalTruckWeightDisplay ? `<div class="header-field"><span class="header-label">Final Wt:</span> <span>${finalTruckWeightDisplay}</span></div>` : ''}
    </div>

    <table>
      <thead>
        <tr>
          <th style="width: 20px;">Sr.</th>
          <th>Size</th>
          <th style="width: 35px;">Pcs</th>
          <th style="width: 50px;">Qty</th>
          <th style="width: 50px;">Act Qty</th>
          <th style="width: 35px;">Act Pcs</th>
        </tr>
      </thead>
      <tbody>
        ${itemsRows}
      </tbody>
    </table>

    <div class="total-qty-row">Total: ${totalQty} ${totalQtyUnit}</div>
    ${order.remark ? `<div style="margin-top: 6px; font-size: 8px; color: #555;"><strong>Remarks:</strong> ${safe(order.remark)}</div>` : ''}
  </div>
</body>
</html>`;

    const w = window.open('', '_blank');
    if (!w) {
      setPrintingOrderId(null);
      alert(t('common.messages.unableToOpenPrintWindow'));
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
    try { w.document.title = ''; } catch (e) { }
    setTimeout(() => { try { w.focus(); w.print(); } catch (e) { } finally { setPrintingOrderId(null); } }, 300);
  }, [t]);

  const handlePrintInvoice = useCallback(async (orderOrId) => {
    const id = typeof orderOrId === 'object' ? orderOrId.id : orderOrId;
    if (!id) return;
    setPrintingInvoiceId(id);
    let order = null;
    try {
      const res = await ordersService.getOrderById(id, { force: true });
      if (res && res.success) order = res.data;
    } catch (e) { }

    if (!order) {
      if (typeof orderOrId === 'object') order = orderOrId;
    }

    if (!order) {
      setPrintingInvoiceId(null);
      alert(t('common.messages.unableToLoadOrderForPrinting'));
      return;
    }

    const safe = (v) => escapeHtml(v); // written into a print window (P1-2)
    const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-GB') : '');

    const freight = Number(order.freight) || 0;
    const freightGst = Number(order.freight_gst_amount) || 0;
    // Freight type billing logic
    const freightTypePrint = order.freight_type || '';
    const isFreightChargeablePrint = freightTypePrint === 'freight_chargeable';
    const isFreightToPayPrint = freightTypePrint === 'free_delivery_freight_to_pay';
    const showFreightRowPrint = !!freightTypePrint;
    const freightPricingTypePrint = order.freight_pricing_type || '';
    const freightLabelPrint = isFreightChargeablePrint
      ? `Freight Chargeable${freightPricingTypePrint === 'per_kg' ? ' (Per KG)' : freightPricingTypePrint === 'fixed' ? ' (Fixed)' : ''}`
      : isFreightToPayPrint ? 'Freight To Pay'
      : freightTypePrint === 'ex_warehouse' ? 'Ex Warehouse'
      : freightTypePrint === 'free_delivery_by_supplier' ? 'Free by Supplier'
      : freightTypePrint === 'free_delivery_paid_by_company' ? 'Freight Paid by Company'
      : 'Freight';
    const freightValuePrint = isFreightToPayPrint
      ? `- ${formatCurrency(freight)}`
      : `${formatCurrency(freight)}`;
    const freightColorPrint = isFreightToPayPrint ? 'color: #059669;' : (!isFreightChargeablePrint ? 'color: #9ca3af;' : '');

    const items = Array.isArray(order.items) ? order.items.filter(it => it && Object.keys(it).length > 0) : [];
    const itemsRows = items.map((it, idx) => `
      <tr>
        <td>${idx + 1}</td>
        <td>
          ${safe(it.product_name)}
          ${it.coil_tracked && safe(it.length_note).trim()
            ? `<div style="font-size: 7px; color: #444; margin-top: 1px;"><b>Length:</b> ${safe(it.length_note).trim()}</div>`
            : ''}
        </td>
        <td>${safe(it.category_name)}</td>
        <td>${safe(it.size_name)}</td>
        <td>${safe(it.hsn_code)}</td>
        <td class="text-right">${safe(Math.ceil(parseFloat(it.quantity || 0)))}</td>
        <td class="text-right">${safe(Math.ceil(parseFloat(it.actual_quantity || 0)))}</td>
        <td>${safe(it.unit)}</td>
        <td class="text-right">${formatCurrency(it.price)}</td>
        <td class="text-right">${formatCurrency(it.amount)}</td>
      </tr>
    `).join('\n');

    const html = `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
      <title>Invoice - ${safe(order.order_number)}</title>
  <style>
    @page { margin: 0; }
    * { box-sizing: border-box; font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; }
    body { margin: 0; padding: 16px; background: #f5f5f5; }
    .no-print { margin-bottom: 16px; }
    .print-container { max-width: 900px; margin: 0 auto; background: #ffffff; padding: 24px 28px; border-radius: 6px; border: 1px solid #e0e0e0; }
    h1,h2,h3,h4 { margin: 0; font-weight: 600; }
    .header { display:flex; justify-content:space-between; gap:16px; margin-bottom:20px; border-bottom:2px solid #e0e0e0; padding-bottom:12px; }
    .header-left h1 { font-size:22px; margin-bottom:6px; }
    .muted { color:#666666; font-size:12px; }
    .section-title { font-size:14px; font-weight:600; margin:18px 0 8px; text-transform:uppercase; letter-spacing:0.03em; }
    .grid-2 { display:grid; grid-template-columns:1fr 1fr; gap:12px 40px; font-size:13px; }
    .field-label { font-size:11px; color:#777777; text-transform:uppercase; letter-spacing:0.05em; }
    .field-value { font-size:13px; margin-top:2px; }
    table { width:100%; border-collapse: collapse; margin-top:8px; font-size:12px; }
    thead { background:#f0f0f0; }
    th,td { padding:6px 8px; border:1px solid #e0e0e0; text-align:left; vertical-align:top; }
    th { font-weight:600; font-size:11px; text-transform:uppercase; letter-spacing:0.05em; }
    .text-right { text-align:right; }
    .summary { margin-top:14px; display:grid; grid-template-columns:1.4fr 1fr; gap:16px; align-items:flex-start; }
    .summary-table { width:100%; border-collapse: collapse; font-size:12px; }
    .summary-table td { padding:4px 0; }
    .summary-table td.label { color:#555555; }
    .summary-table td.value { text-align:right; font-weight:500; }
    .summary-table tr.total-row td { border-top:1px solid #cccccc; font-weight:600; padding-top:6px; }
    .remark-box { margin-top:12px; padding:8px 10px; border-radius:4px; background:#fafafa; border:1px dashed #cccccc; font-size:12px; }
    .footer { margin-top:24px; padding-top:10px; border-top:1px solid #e0e0e0; font-size:11px; color:#777777; display:flex; justify-content:space-between; gap:16px; }
    @media print { body { padding:0; background:#ffffff } .no-print { display:none !important } .print-container { border:none; border-radius:0; max-width:100%; padding:16px 24px } }
  </style>
</head>
<body>
  <div class="no-print"><button onclick="window.print()">Print</button></div>
  <div class="print-container">
    <div class="header">
      <div class="header-left">
      <h1>Invoice</h1>
        <div class="muted">
          Order Number: ${safe(order.order_number)}<br />
        </div>
      </div>
      <div class="header-right" style="text-align:right; font-size:12px;">
        ${order.date ? `<div><strong>Order Date:</strong> ${fmt(order.date)}</div>` : ''}
        ${order.expected_delivery_date ? `<div><strong>Expected Delivery Date:</strong> ${fmt(order.expected_delivery_date)}</div>` : ''}
        ${order.invoice_date ? `<div><strong>Invoice Date:</strong> ${fmt(order.invoice_date)}</div>` : ''}
        ${order.invoice_number ? `<div><strong>Invoice Number:</strong> ${safe(order.invoice_number)}</div>` : ''}
      </div>
    </div>

    <div class="section-title">Parties</div>
    <div class="grid-2">
      <div>
        <div class="field-label">Customer</div>
        <div class="field-value">
          ${safe(order.customer_name)}<br />
          ${order.tally_name ? `Accounting Name: ${safe(order.tally_name)}<br />` : ''}
          ${order.delivery_location_name ? `Delivery Location: ${safe(order.delivery_location_name)}` : ''}
        </div>
      </div>
      <div>
        ${(order.supplier_name || order.supervisor_name) ? `
        <div class="field-label">Supplier</div>
        <div class="field-value">
          ${order.supplier_name ? `${safe(order.supplier_name)}<br />` : ''}
          ${order.supervisor_name ? `Delivery Supervisor: ${safe(order.supervisor_name)}` : ''}
        </div>` : ''}
      </div>
    </div>

    <div class="section-title">Logistics</div>
    <div class="grid-2">
      <div>
        ${(order.vehicle_number || order.driver_contact) ? `
        <div class="field-label">Vehicle</div>
        <div class="field-value">
          ${order.vehicle_number ? `Vehicle Number: ${safe(order.vehicle_number)}<br />` : ''}
          ${order.driver_contact ? `Driver Contact: ${safe(order.driver_contact)}` : ''}
        </div>` : ''}
      </div>
      <div>
        ${(order.empty_truck_weight || order.final_truck_weight || order.truck_weight) ? `
        <div class="field-label">Weights</div>
        <div class="field-value">
          ${order.empty_truck_weight ? `Empty Truck Weight: ${safe(order.empty_truck_weight)} kg<br />` : ''}
          ${order.final_truck_weight ? `Final Truck Weight: ${safe(order.final_truck_weight)} kg<br />` : ''}
          ${order.truck_weight ? `Loaded Material Weight: ${safe(order.truck_weight)} kg` : ''}
        </div>` : ''}
      </div>
    </div>

    <div class="section-title">Items (Total: ${items.length})</div>
    <table>
      <thead>
        <tr>
          <th>#</th>
          <th>Product</th>
          <th>Category</th>
          <th>Size</th>
          <th>HSN</th>
          <th class="text-right">Quantity</th>
          <th class="text-right">Actual Quantity</th>
          <th>Unit</th>
          <th class="text-right">Price</th>
          <th class="text-right">Amount</th>
        </tr>
      </thead>
      <tbody>
        ${itemsRows || '<tr><td colspan="10">No items</td></tr>'}
      </tbody>
    </table>

    <div class="summary">
      <div>

        <div class="section-title">Tax and Charges</div>
        <div style="font-size:12px;">
          GST Option: ${safe(order.gst_option)}<br />
          TCS Applicable: ${order.tcs_applicable ? 'Yes' : 'No'} (Rate: ${safe(order.tcs_percentage)} percent, Amount: ${formatCurrency(order.tcs_amount)})
        </div>
      </div>

      <div>
        <table class="summary-table">
          <tr>
            <td class="label">Subtotal</td>
            <td class="value">${formatCurrency(order.subtotal)}</td>
          </tr>
          ${showFreightRowPrint ? `
          <tr>
            <td class="label" style="${freightColorPrint}">${freightLabelPrint}</td>
            <td class="value" style="${freightColorPrint}">${freightValuePrint}</td>
          </tr>
          ${isFreightChargeablePrint && freightGst > 0 ? `
          <tr>
            <td class="label" style="padding-left: 10px; font-size: 11px; color: #6b7280;">Freight GST</td>
            <td class="value" style="font-size: 11px; color: #6b7280;">${formatCurrency(freightGst)}</td>
          </tr>` : ''}` : ''}
          ${order.loading_cost_per_kg && parseFloat(order.loading_cost_per_kg) > 0 ? `
          <tr>
            <td class="label">Loading Cost</td>
            <td class="value">${formatCurrency(order.loading_cost_per_kg)}</td>
          </tr>` : ''}
          ${order.discount_amount && parseFloat(order.discount_amount) > 0 ? `
          <tr>
            <td class="label">Discount (${safe(order.discount_type)})</td>
            <td class="value">- ${formatCurrency(order.discount_amount)}</td>
          </tr>` : ''}
          ${order.total_sub_order_expenses && parseFloat(order.total_sub_order_expenses) > 0 ? `
          <tr>
            <td class="label">Expenses</td>
            <td class="value">${formatCurrency(order.total_sub_order_expenses)}</td>
          </tr>` : ''}
          ${order.total_sub_order_discount && parseFloat(order.total_sub_order_discount) > 0 ? `
          <tr>
            <td class="label">Discount</td>
            <td class="value">- ${formatCurrency(order.total_sub_order_discount)}</td>
          </tr>` : ''}
          ${order.tcs_amount && parseFloat(order.tcs_amount) > 0 ? `
          <tr>
            <td class="label">TCS</td>
            <td class="value">${formatCurrency(order.tcs_amount)}</td>
          </tr>` : ''}
          <tr class="total-row">
            <td class="label">Total Amount</td>
            <td class="value">${formatCurrency(order.total_amount)}</td>
          </tr>
        </table>
      </div>
    </div>

    ${order.remark ? `<div class="remark-box"><strong>Remarks:</strong> ${safe(order.remark)}</div>` : ''}

  </div>
</body>
</html>`;

    const w = window.open('', '_blank');
    if (!w) {
      setPrintingInvoiceId(null);
      alert(t('common.messages.unableToOpenPrintWindow'));
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
    try { w.document.title = ''; } catch (e) { }
    setTimeout(() => { try { w.focus(); w.print(); } catch (e) { } finally { setPrintingInvoiceId(null); } }, 300);
  }, [t]);

  const handlePrintGenericOrder = useCallback(async (orderOrId) => {
    const id = typeof orderOrId === 'object' ? orderOrId.id : orderOrId;
    if (!id) return;
    try {
      setGlobalLoading(true);
      const res = await ordersService.getOrderById(id, { force: true });
      if (res && res.success && res.data) {
        import('../../utils/printHelpers').then(({ printDocument }) => {
          printDocument(res.data, 'Order');
        });
      } else {
        alert(t('common.messages.failedToLoadOrderForPrinting'));
      }
    } catch(e) {
      alert(t('common.messages.failedToLoadOrderForPrinting'));
    } finally {
      setGlobalLoading(false);
    }
  }, [t]);

  const columns = useMemo(() => [
    {
      id: 'selection',
      sortable: false,
      width: '40px',
      renderHeader: () => {
        const currentPageIds = orders.map(o => o.id);
        const isAllSelected = currentPageIds.length > 0 && currentPageIds.every(id => selectedOrders.includes(id));
        return (
          <input 
            type="checkbox" 
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
            checked={isAllSelected}
            onChange={(e) => onSelectAll?.(e.target.checked)} 
          />
        );
      },
      renderCell: (o) => (
        <input 
          type="checkbox" 
          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
          checked={selectedOrders.includes(o.id)}
          onChange={(e) => onSelectOrder?.(o.id, e.target.checked)} 
        />
      ),
      skeleton: <div className="w-4 h-4 bg-gray-300 rounded" />,
    },
    {
      id: 'order_number',
      label: t('ordersModule.orders.table.columns.orderNumber'),
      sortable: true,
      sortAccessor: (o) => (o?.order_number || '').toLowerCase(),
      renderCell: (o) => (
        <div className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{o.order_number ? o.order_number : t('ordersModule.common.symbols.emDash')}</div>
      ),
      skeleton: <div className="w-32 h-4 bg-gray-300 rounded" />,
    },
    {
      id: 'date',
      label: t('ordersModule.orders.table.columns.date'),
      sortable: true,
      sortAccessor: (o) => (o?.date ? new Date(o.date).getTime() : 0),
      renderCell: (o) => <span className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{o.date ? formatDate(o.date, t('ordersModule.common.symbols.emDash')) : t('ordersModule.common.symbols.emDash')}</span>,
      skeleton: <div className="w-20 h-4 bg-gray-300 rounded" />,
    },
    {
      id: 'tally_name',
      label: t('ordersModule.orders.table.columns.tallyName'),
      sortable: true,
      sortAccessor: (o) => (o?.tally_name || '').toLowerCase(),
      renderCell: (o) => (
        <div className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
          {o.tally_name || o.customer_name || t('ordersModule.common.symbols.emDash')}
        </div>
      ),
      skeleton: <div className="w-40 h-4 bg-gray-300 rounded" />,
    },
    {
      id: 'calculated_total_weight',
      label: t('ordersModule.orders.table.columns.totalWeight'),
      sortable: true,
      sortAccessor: (o) => (o?.calculated_total_weight ? parseFloat(o.calculated_total_weight) : 0),
      renderCell: (o) => <span className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{o.calculated_total_weight ? `${o.calculated_total_weight} KG` : t('ordersModule.common.symbols.emDash')}</span>,
      skeleton: <div className="w-24 h-4 bg-gray-300 rounded" />,
    },
    {
      id: 'delivery_location_name',
      label: t('ordersModule.orders.table.columns.deliveryLocation'),
      sortable: true,
      sortAccessor: (o) => (o?.delivery_location_name || '').toLowerCase(),
      renderCell: (o) => <span className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{o.delivery_location_name ? o.delivery_location_name : t('ordersModule.common.symbols.emDash')}</span>,
      skeleton: <div className="w-32 h-4 bg-gray-300 rounded" />,
    },
    // {
    //   id: 'supervisor_name',
    //   label: t('commonCols.supervisor'),
    //   sortable: true,
    //   sortAccessor: (o) => (o?.supervisor_name || '').toLowerCase(),
    //   renderCell: (o) => <span className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{o.supervisor_name ? o.supervisor_name : t('ordersModule.common.symbols.emDash')}</span>,
    //   skeleton: <div className="w-32 h-4 bg-gray-300 rounded" />,
    // },
    {
      id: 'vehicle_number',
      label: t('ordersModule.orders.table.columns.vehicleNumber'),
      sortable: true,
      sortAccessor: (o) => (o?.vehicle_number || '').toLowerCase(),
      renderCell: (o) => (
        <span className="text-sm font-medium uppercase" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
          {o.vehicle_number ? o.vehicle_number : t('ordersModule.common.symbols.emDash')}
        </span>
      ),
      skeleton: <div className="w-24 h-4 bg-gray-300 rounded" />,
    },
    {
      id: 'remark',
      label: t('commonCols.remarks'),
      sortable: true,
      sortAccessor: (o) => (o?.remark || '').toLowerCase(),
      renderCell: (o) => {
        if (!o.remark) return <span style={{ color: UI_COLORS.TEXT.MUTED }}>{t('ordersModule.common.symbols.emDash')}</span>;
        return (
          <div 
            className="text-[11px] font-medium break-words leading-relaxed min-w-[160px] max-w-[180px] px-2 py-1.5 rounded border" 
            title={o.remark} 
            style={{ 
              backgroundColor: 'rgba(254, 243, 199, 0.5)', 
              color: '#92400e', 
              borderColor: 'rgba(217, 119, 6, 0.2)',
              whiteSpace: 'pre-wrap',
              display: '-webkit-box',
              WebkitLineClamp: 5,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}
          >
            {o.remark}
          </div>
        );
      },
      skeleton: <div className="w-32 h-4 bg-gray-300 rounded" />,
    },
    {
      id: 'status',
      label: t('ordersModule.orders.table.columns.status'),
      sortable: true,
      align: 'center',
      sortAccessor: (o) => (o?.status || '').toLowerCase(),
      renderCell: (o) => getStatusBadge(o.status, t),
      skeleton: <div className="w-24 h-4 bg-gray-300 rounded" />,
    },
    {
      id: 'status_actions',
      label: t('ordersModule.orders.table.columns.statusActions'),
      align: 'center',
      sortable: false,
      renderCell: (o) => {
        const statusStr = (o?.status || '').toString().toLowerCase();

        return (
          <div className="flex flex-wrap items-center justify-center gap-2">
            {/* Confirm Order - only for pending status */}
            {statusStr === 'pending' && userRole !== 'DELIVERY_SUPERVISOR' && (
              <button
                onClick={() => {
                  setConfirmOrderId(o.id);
                  setShowConfirmOrder(true);
                }}
                className="px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 whitespace-nowrap"
                style={{ color: UI_COLORS.STATUS.SUCCESS, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.SUCCESS, 0.12) }}
                title={t('common.actions.confirmOrder')}
              >
                <FontAwesomeIcon icon={faCheck} className="text-xs" />
                <span className="text-xs font-medium">{t('ordersModule.orders.actions.buttons.confirm')}</span>
              </button>
            )}


            {/* Confirmed / Delivery Assigned - Show navigation to suborders */}
            {['confirmed', 'delivery_assigned'].includes(statusStr) && (
              <button
                onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: { page: 'delivery-orders' } }))}
                className="px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 whitespace-nowrap"
                style={{ color: UI_COLORS.STATUS.INFO, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.INFO, 0.12) }}
                title={t('common.actions.goToDeliveryOrders')}
              >
                <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                <span className="text-xs font-medium">{t('ordersModule.orders.messages.completeSubOrders')}</span>
              </button>
            )}



            {/* Generate Challan / Mark Delivered live on the Delivery Orders page. */}

            {/* Print Delivery Order - Only when loading */}
            {['loading', 'truck_loading', 'loading_started', 'truck_load_started', 'truck_loading_started', 'loading_in_progress', 'truck_load'].includes(statusStr) && (
              <button
                onClick={() => handlePrintDeliveryOrder(o.id)}
                disabled={printingOrderId === o.id}
                className="px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 whitespace-nowrap"
                style={{ color: UI_COLORS.TEXT.PRIMARY, backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.12) }}
                title={t('ordersModule.orders.table.tooltips.printDeliveryOrder')}
              >
                {printingOrderId === o.id ? (
                  <FontAwesomeIcon icon={faSpinner} spin className="text-xs" />
                ) : (
                  <FontAwesomeIcon icon={faPrint} className="text-xs" />
                )}
                <span className="text-xs font-medium">{t('ordersModule.orders.table.tooltips.printDeliveryOrder')}</span>
              </button>
            )}

            {/* Print Delivery Challan */}
            {statusStr === 'in_transit' && (
              <button
                onClick={() => handlePrintChallan(o.id)}
                disabled={printingOrderId === o.id}
                className="px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 whitespace-nowrap"
                style={{ color: UI_COLORS.TEXT.PRIMARY, backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.12) }}
                title={t('ordersModule.orders.table.tooltips.printDeliveryChallan')}
              >
                {printingOrderId === o.id ? (
                  <FontAwesomeIcon icon={faSpinner} spin className="text-xs" />
                ) : (
                  <FontAwesomeIcon icon={faPrint} className="text-xs" />
                )}
                <span className="text-xs font-medium">{t('ordersModule.orders.table.tooltips.printDeliveryChallan')}</span>
              </button>
            )}

            {/* "Generate Invoice" removed: its backend route
                (POST /orders/{id}/generate-invoice) no longer exists, so the button
                could only ever fail. Invoicing happens through the challan flow. */}

            {/* Print Invoice */}
            {statusStr === 'invoiced' && (
              <button
                onClick={() => handlePrintInvoice(o.id)}
                disabled={printingInvoiceId === o.id}
                className="px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 whitespace-nowrap"
                style={{ color: UI_COLORS.TEXT.PRIMARY, backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.12) }}
                title={t('ordersModule.orders.table.tooltips.printInvoice')}
              >
                {printingInvoiceId === o.id ? (
                  <FontAwesomeIcon icon={faSpinner} spin className="text-xs" />
                ) : (
                  <FontAwesomeIcon icon={faPrint} className="text-xs" />
                )}
                <span className="text-xs font-medium">{t('ordersModule.orders.table.tooltips.printInvoice')}</span>
              </button>
            )}
          </div>
        );
      },
      skeleton: (
        <div className="flex items-center justify-center space-x-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="w-8 h-8 bg-gray-300 rounded-lg" />
          ))}
        </div>
      ),
    },
    {
      id: 'actions',
      label: t('ordersModule.orders.table.columns.actions'),
      align: 'center',
      sortable: false,
      renderCell: (o) => {
        const statusStr = (o?.status || '').toString().toLowerCase();

        return (
          <div className="flex items-center justify-center space-x-2">
            <button
              onClick={() => onViewOrder?.(o.id)}
              disabled={viewLoading}
              className="p-2 rounded-lg transition-all duration-200 hover:scale-105 disabled:opacity-50"
              style={{ color: UI_COLORS.TEXT.PRIMARY, backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.35) }}
              title={t('ordersModule.orders.table.tooltips.view')}
            >
              <FontAwesomeIcon icon={faEye} className="text-sm" />
            </button>

            <button
              onClick={() => handlePrintGenericOrder(o.id)}
              className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
              style={{ color: UI_COLORS.TEXT.PRIMARY, backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.35) }}
              title={t('common.actions.printOrder')}
            >
              <FontAwesomeIcon icon={faPrint} className="text-sm" />
            </button>

            {/* Audit Logs / timeline — admin, sales and operations manager.
                Not the delivery supervisor: the trail names who assigned the job. */}
            {AUDIT_LOG_VIEWER_ROLES.includes(userRole) && (
              <button
                onClick={() => setAuditLogTarget({ id: o.id, label: o.order_number })}
                className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
                style={{ color: UI_COLORS.STATUS.INFO, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.INFO, 0.1) }}
                title={t('common.actions.auditLogs')}
              >
                <FontAwesomeIcon icon={faHistory} className="text-sm" />
              </button>
            )}


            {/* Edit Button - hide from supervisor */}
            {[
              'pending', 'confirmed', 'delivery_assigned', 
              'loading', 'loading_started', 'truck_loading', 'truck_load_started', 'truck_loading_started', 'loading_in_progress', 'truck_load',
              'loaded', 'truck_loaded', 'truck_load_completed'
            ].includes(statusStr) && userRole !== 'DELIVERY_SUPERVISOR' && !canEditOrderRow(o) && editBlockedReasonRow(o) && (
              <button
                disabled
                className="p-2 rounded-lg opacity-40 cursor-not-allowed"
                style={{ color: UI_COLORS.STATUS.WARNING, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.WARNING, 0.1) }}
                title={editBlockedReasonRow(o)}
              >
                <FontAwesomeIcon icon={faEdit} className="text-sm" />
              </button>
            )}

            {[
              'pending', 'confirmed', 'delivery_assigned',
              'loading', 'loading_started', 'truck_loading', 'truck_load_started', 'truck_loading_started', 'loading_in_progress', 'truck_load',
              'loaded', 'truck_loaded', 'truck_load_completed'
            ].includes(statusStr) && userRole !== 'DELIVERY_SUPERVISOR' && canEditOrderRow(o) && (
              <button
                onClick={() => onEditOrder?.(o.id)}
                className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
                style={{ color: UI_COLORS.STATUS.WARNING, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.WARNING, 0.1) }}
                title={t('ordersModule.orders.table.tooltips.edit')}
              >
                <FontAwesomeIcon icon={faEdit} className="text-sm" />
              </button>
            )}

            {/* Delete Button - hide from supervisor */}
            {userRole !== 'DELIVERY_SUPERVISOR' && !canDeleteOrderRow(o) && deleteBlockedReasonRow(o) && (
              <button
                disabled
                className="p-2 rounded-lg opacity-40 cursor-not-allowed"
                style={{ color: UI_COLORS.STATUS.ERROR, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1) }}
                title={deleteBlockedReasonRow(o)}
              >
                <FontAwesomeIcon icon={faTrash} className="text-sm" />
              </button>
            )}

            {canDeleteOrderRow(o) && userRole !== 'DELIVERY_SUPERVISOR' && (
              <button
                onClick={() => onDeleteOrder?.(o.id)}
                className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
                style={{ color: UI_COLORS.STATUS.ERROR, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1) }}
                title={t('ordersModule.orders.table.tooltips.delete')}
              >
                <FontAwesomeIcon icon={faTrash} className="text-sm" />
              </button>
            )}

            {/* Cancel Order is hidden (policy): cancel the delivery orders instead —
                the parent order follows once every DO is cancelled. */}
          </div>
        );
      },
      skeleton: (
        <div className="flex items-center justify-center space-x-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="w-8 h-8 bg-gray-300 rounded-lg" />
          ))}
        </div>
      ),
    },
  ], [
    handlePrintGenericOrder,
    onDeleteOrder,
    onEditOrder,
    onViewOrder,
    handlePrintChallan,
    handlePrintDeliveryOrder,
    handlePrintInvoice,
    printingOrderId,
    printingInvoiceId,
    globalLoading,
    onRefresh,
    showError,
    showSuccess,
    viewLoading,
    checkDeleteAccess,
    checkEditAccess,
    canEditOrderRow,
    canDeleteOrderRow,
    editBlockedReasonRow,
    deleteBlockedReasonRow,
    userRole,
    setAuditLogTarget,
    orders,
    selectedOrders,
    onSelectAll,
    onSelectOrder,
    t,
  ]);

  const getRowProps = useCallback((/* order */) => {
    return {
      className: 'hover:bg-opacity-50 transition-colors',
      style: { backgroundColor: 'transparent' },
      onMouseEnter: (event) => { event.currentTarget.style.backgroundColor = hoverColor; },
      onMouseLeave: (event) => { event.currentTarget.style.backgroundColor = 'transparent'; },
    };
  }, [hoverColor]);

  const emptyState = useMemo(() => ({
    icon: faShoppingCart,
    title: t('ordersModule.orders.table.empty.title'),
    message: t('ordersModule.orders.table.empty.message'),
  }), [t]);

  // Hide orders where GST is included and status is 'invoiced'
  // Show all orders including 'invoiced'
  const visibleOrders = useMemo(() => {
    if (!Array.isArray(orders)) return [];
    return orders;
  }, [orders]);

  return (
    <>
      {/* Full screen loading overlay when performing global actions */}
      {globalLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.OVERLAY, 0.7) }}>
          <div className="flex flex-col items-center">
            <div className="h-16 w-16 animate-spin rounded-full border-4 border-b-transparent" style={{ borderColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.7) }}></div>
            <p className="mt-4 text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{t('ordersModule.common.status.processing')}</p>
          </div>
        </div>
      )}
      <DataTable
        columns={columns}
        data={visibleOrders}
        loading={loading}
        keyField="id"
        enablePagination={false}
        defaultSort={{ field: 'updated_at', direction: 'desc' }}
        getRowProps={getRowProps}
        emptyState={emptyState}
      />

      <AuditLogsModal
        isOpen={!!auditLogTarget}
        onClose={() => setAuditLogTarget(null)}
        entityType="order"
        entityId={auditLogTarget?.id}
        entityLabel={auditLogTarget?.label}
        hasTimeline={true}
      />

      <ConfirmDialog
        isOpen={showConfirmOrder}
        title={t('ordersModule.orders.confirm.confirmOrder.title')}
        message={t('ordersModule.orders.confirm.confirmOrder.message')}
        confirmLabel={t('ordersModule.orders.confirm.confirmOrder.confirmLabel')}
        loading={globalLoading}
        loadingLabel={t('ordersModule.common.buttons.loading')}
        confirmVariant="success"
        onCancel={() => { setShowConfirmOrder(false); setConfirmOrderId(null); }}
        onConfirm={async () => {
          const id = confirmOrderId;
          if (!id) return;
          setGlobalLoading(true);
          try {
            const res = await ordersService.confirmOrder(id);
            if (res && res.success) {
              showSuccess(t('ordersModule.orders.messages.confirmOrderSuccess'));
              if (onRefresh) onRefresh();
            } else {
              showError(t('ordersModule.orders.messages.confirmOrderFailed') + (res?.error ? `: ${res.error}` : ''));
            }
          } catch (e) {
            showError(t('ordersModule.orders.messages.confirmOrderFailed'));
          } finally {
            setGlobalLoading(false);
            setShowConfirmOrder(false);
            setConfirmOrderId(null);
          }
        }}
      />


    </>
  );
};

export default Orderstable;
